import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import {
	Priority,
	SourcingRequestStatus,
	SourcingRequestType,
} from '../../enums';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { OrderEntity } from './order.entity';
import { SourcingFulfillmentAllocationEntity } from './sourcing-fulfillment-allocation.entity';
import type { SourcingRequestActivityEntity } from './sourcing-request-activity.entity';
import { SourcingRequestFileEntity } from './sourcing-request-file.entity';
import { SourcingRequestLineEntity } from './sourcing-request-line.entity';
import { SourcingRequestNoteEntity } from './sourcing-request-note.entity';
import type { SourcingRequestUserEntity } from './sourcing-request-user.entity';
import { UserEntity } from './user.entity';

@Index('idx_sr_company', ['fkCompanyId'], {})
@Index('idx_sr_client', ['fkClientId'], {})
@Index('idx_sr_order', ['fkOrderId'], {})
@Index('idx_sr_assigned', ['fkUserId'], {})
@Index('idx_sr_status', ['status'], {})
@Index('idx_sr_type', ['type'], {})
@Index('idx_sr_target_date', ['targetDate'], {})
@Entity('sourcing_request')
export class SourcingRequestEntity extends BaseEntity {
	@Column('int', { name: '_fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('enum', {
		name: 'type',
		enum: SourcingRequestType,
		default: SourcingRequestType.ORDER,
	})
	public type!: SourcingRequestType;

	@Column('enum', {
		name: 'status',
		enum: SourcingRequestStatus,
		default: SourcingRequestStatus.DRAFT,
	})
	public status!: SourcingRequestStatus;

	@Column('enum', {
		name: 'priority',
		enum: Priority,
		default: Priority.NORMAL,
	})
	public priority!: Priority;

	@Column('int', { name: '_fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	@Column('int', { name: '_fk_order__id', nullable: true, unsigned: true })
	public fkOrderId!: number | null;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('date', { name: 'target_date', nullable: true })
	public targetDate!: Date | null;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('int', { name: 'total_lines', unsigned: true, default: 0 })
	public totalLines!: number;

	@Column('int', { name: 'lines_pending', unsigned: true, default: 0 })
	public linesPending!: number;

	@Column('int', { name: 'lines_fulfilled', unsigned: true, default: 0 })
	public linesFulfilled!: number;

	/**
	 * Count of lines currently in `pending_info` status. Maintained
	 * by `updateLineCounts` alongside totalLines / linesPending /
	 * linesFulfilled. Backs the "awaiting info" sort key so SRs
	 * blocked on requestor clarification float to the top of the
	 * default My Requests view (see `myRequestsList.defaultSortId`).
	 * Stored as a column rather than derived per-query to keep the
	 * sort fast on large SR lists.
	 */
	@Column('int', { name: 'lines_pending_info', unsigned: true, default: 0 })
	public linesPendingInfo!: number;

	/**
	 * Sum of `line.estimatedCost * line.quantityRequested` across all
	 * non-cancelled, non-deleted lines on this SR. Computed and stored
	 * by `updateLineCounts` in the SR service — same sync pattern as
	 * totalLines / linesPending / linesFulfilled. Surfaced on the
	 * Overview tab, kanban card, and SR list table column. Null is
	 * stored when no lines have a cost yet (vs storing 0 which would
	 * imply a $0 budget).
	 */
	@Column('decimal', {
		name: 'total_estimated_cost',
		precision: 15,
		scale: 4,
		nullable: true,
	})
	public totalEstimatedCost!: string | null;

	/**
	 * Sum of `line.actualCost * line.quantityReceived` across received
	 * lines. Updated alongside totalEstimatedCost. Lets finance see
	 * "what did this SR actually cost us once everything came in".
	 */
	@Column('decimal', {
		name: 'total_actual_cost',
		precision: 15,
		scale: 4,
		nullable: true,
	})
	public totalActualCost!: string | null;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_company__id', referencedColumnName: 'id' }])
	public fkCompany!: CompanyEntity;

	@ManyToOne(() => ClientEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client__id', referencedColumnName: 'id' }])
	public fkClient!: ClientEntity | null;

	@ManyToOne(() => OrderEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_order__id', referencedColumnName: 'id' }])
	public fkOrder!: OrderEntity | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity | null;

	@OneToMany(
		() => SourcingRequestLineEntity,
		(line) => line.fkSourcingRequest,
		{},
	)
	public lines!: Array<SourcingRequestLineEntity>;

	@OneToMany(
		() => SourcingRequestNoteEntity,
		(note) => note.fkSourcingRequest,
		{},
	)
	public notes_!: Array<SourcingRequestNoteEntity>;

	@OneToMany('SourcingRequestActivityEntity', 'fkSourcingRequest', {})
	public activities!: Array<SourcingRequestActivityEntity>;

	@OneToMany(
		() => SourcingFulfillmentAllocationEntity,
		(allocation) => allocation.fkSourcingRequestLine,
		{},
	)
	public fulfillmentAllocations!: Array<SourcingFulfillmentAllocationEntity>;

	@OneToMany(
		'SourcingRequestUserEntity',
		(user: SourcingRequestUserEntity) => user.fkSourcingRequest,
		{},
	)
	public users!: Array<SourcingRequestUserEntity>;

	@OneToMany(
		() => SourcingRequestFileEntity,
		(file) => file.fkSourcingRequest,
		{},
	)
	public sourcingRequestFiles!: Array<SourcingRequestFileEntity>;
}
